import { useDebouncedSearch, useLoading } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useDebounceFunction } from '../../hooks/useDebounce';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import { getVehicleSubBrandsList } from '../../services/vehicleService';
import { apiParamsState, isAllWorkspacesSelect } from '../../storage/app';
import { SubBrand } from '../../types/subBrand';
import { EnhancedLabeledSelect } from '../ui/EnhancedLabeledSelect';

type Props = {
  onChangeSubBrand: (subBrandId?: string) => void;
  clearable?: boolean;
  disabled?: boolean;
  emptyOptionText?: string;
  error?: React.ReactNode;
  inline?: boolean;
  isFilter?: boolean;
  label?: string;
  onBlur?: () => void;
  required?: boolean;
  selectedBrandId?: string;
  selectedSubBrandId?: string;
  showEmptyOption?: boolean;
  subBrand?: SubBrand;
};
export function SubBrandSelect({
  error,
  inline,
  onBlur,
  onChangeSubBrand,
  selectedBrandId,
  selectedSubBrandId,
  subBrand,
  clearable = false,
  disabled = false,
  emptyOptionText = 'Selecciona una submarca',
  isFilter = false,
  label = 'Submarca:',
  required = false,
  showEmptyOption = false,
}: Readonly<Props>) {
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const apiParams = useRecoilValue(apiParamsState);
  const [subBrandsList, setSubBrandsList] = useState<SubBrand[]>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const { search, setSearch, trigger: debouncedSearch } = useDebouncedSearch('');
  const [brandId, setBrandId] = useState<string>();
  const [selectedSubBrandOption, setSelectedSubBrandOption] = useState<{ id: string; label: string }>();
  const [initialBrandSearch, setInitialBrandSearch] = useState(false);
  const requestIdRef = useRef(0);

  useUpdateEffect(() => {
    setPage(0);
  }, [debouncedSearch, brandId, apiParams?.clientId, apiParams?.subClientId]);

  useEffect(() => {
    if (!apiParams) return;
    if (page === 0) {
      setPage(1);
      return;
    }
    const loadSubBrands = async () => {
      startLoading();
      const requestId = ++requestIdRef.current;
      const { subBrands, error, hasNext } = await getVehicleSubBrandsList({
        clientId: apiParams.clientId,
        subClientId: !isAllWorkspacesSelected ? apiParams.subClientId : undefined,
        page,
        search,
        brandId,
      });
      if (requestId === requestIdRef.current) {
        if (!error && subBrands) {
          if (page > 1 && subBrandsList) {
            setSubBrandsList([...subBrandsList, ...subBrands]);
            setHasNextPage(hasNext);
          } else {
            setSubBrandsList(subBrands);
            setHasNextPage(hasNext);
          }
        } else {
          setSubBrandsList(undefined);
          setHasNextPage(false);
        }
        stopLoading();
      }
    };
    loadSubBrands();
  }, [page]);

  useEffect(() => {
    if (brandId && selectedSubBrandId) onChangeSubBrand();
    setBrandId(selectedBrandId);
  }, [selectedBrandId]);

  const subBrandOptions = useMemo(() => {
    if (!subBrandsList) return;
    const subBrandsOptions: { id: string | undefined; label: string }[] = subBrandsList.map((subBrand) => ({
      id: subBrand._id,
      label: subBrand.name,
    }));
    if (!required) {
      subBrandsOptions?.unshift({
        id: undefined,
        label: isFilter ? 'Todas' : emptyOptionText,
      });
    }
    if (subBrand) {
      if (subBrand._id && !subBrandsOptions.find((c) => c.id === subBrand._id)) {
        const subBrandOption = {
          id: subBrand._id,
          label: subBrand.name,
        };
        subBrandsOptions.push(subBrandOption);
        onChangeSubBrand(subBrandOption.id);
        setSelectedSubBrandOption(subBrandOption);
      } else if (subBrand.name && !subBrand._id) {
        const subBrandFind = subBrandsOptions.find(
          (c) => c.label.toLowerCase().trim() === subBrand.name.toLowerCase().trim()
        );
        if (subBrandFind) {
          onChangeSubBrand(subBrandFind.id);
          setSelectedSubBrandOption(subBrandFind as { id: string; label: string });
        } else if (search !== subBrand.name && !initialBrandSearch) {
          setSearch(subBrand.name);
          setInitialBrandSearch(true);
        } else {
          setSearch('');
          onChangeSubBrand(undefined);
        }
      }
    }
    if (selectedSubBrandOption) {
      if (!subBrandsOptions.find((c) => c.id === selectedSubBrandOption.id))
        subBrandsOptions.push(selectedSubBrandOption);
    }
    return subBrandsOptions;
  }, [subBrandsList, subBrand]);

  const onSubBrandChange = (params: OnChangeParams) => {
    params.option?.id ? onChangeSubBrand(params.option.id as string) : onChangeSubBrand(undefined);
    setSelectedSubBrandOption(params.option as { id: string; label: string });
    setSearch(undefined);
  };

  const fetchData = () => {
    if (hasNextPage) setPage((page ?? 0) + 1);
  };

  const handleInputChange = useDebounceFunction(function (term: string) {
    setSearch(term);
  }, 350);

  return (
    <EnhancedLabeledSelect
      label={label}
      options={subBrandOptions}
      value={showEmptyOption || selectedSubBrandId !== undefined ? [{ id: selectedSubBrandId }] : undefined}
      onChange={onSubBrandChange}
      onInputChange={(e) => {
        handleInputChange(e.target.value);
      }}
      clearable={clearable}
      disabled={disabled}
      error={error}
      fetchData={fetchData}
      fetchDataLoading={loading}
      fullWidth
      hasNext={hasNextPage}
      infiniteScroll
      inline={inline}
      inset={inline}
      placeholder="Selecciona una submarca"
      onBlur={() => {
        setSearch(undefined);
        onBlur && onBlur();
      }}
      required={required}
      searchable
    />
  );
}
